/**
 * Pause and resume — `client/widget-*.html` §2's button row, and the state it
 * leads to.
 *
 * `.docs/13` §Widgets: pausing "takes effect immediately and can be undone here;
 * events already recorded still bill". Three consequences shape this file:
 *
 *   - **Pausing goes through a confirm dialog and resuming does not.** Pausing
 *     stops a live site from serving; resuming puts back what was there. A
 *     confirmation on the undo is the chrome P7 deletes.
 *   - **The paused state is a `Notice`, not a `Pill`, and it is not ochre.**
 *     DESIGN.md P4: ochre is "this needs your decision, or this costs you more",
 *     and a widget somebody paused on purpose is neither. CONTRACT.md §4.3 rules
 *     out a chip whose word the sentence already carries.
 *   - **Neither button is drawn for a role the API would refuse.**
 *     `usage.service` `assertMayManageWidget` is `money:approve` — "a cap and a
 *     top-up instruction are money decisions the **client** makes … an owner or an
 *     admin may, a `member` may not" — and pausing is on the same rule, because
 *     stopping a widget stops what it earns. `.docs/19` §Edge cases asks for the
 *     button to be **absent**, and the cap control on the same subject
 *     (`settings/WidgetCapsPanel`) has been gated that way since session 19; this
 *     one never imported `usePrincipal` at all, so a `billing` or `member` seat got
 *     a live button and a 403.
 */

import { isMetered, isZero, type WidgetSummaryView, type WidgetView } from '@fareast/shared'
import { useState } from 'react'

import { useSetWidgetPaused } from '../../../api/client/widgets.js'
import { usePrincipal } from '../../../auth/principal.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Linkish, Muted, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { Link } from '@tanstack/react-router'
import { abilitiesOf } from '../settings/roles.js'
import { WIDGET_CAPS_ANCHOR } from '../settings/WidgetCapsPanel.js'
import { nounOf } from './text.js'

/**
 * The anchor on `/app/settings` that owns the cap form.
 *
 * "Change the cap" links rather than editing: `client/settings.html` §Widget caps is
 * where a cap is set, and its own note says the widget pages "own this month's usage;
 * they are linked, not repeated".
 *
 * **The constant lives with the section that renders the id**, and is re-exported here
 * so the three call sites on this page keep their import. It was declared here and
 * written as a literal there, which is one rename away from three dead links and no
 * failing test.
 */
export { WIDGET_CAPS_ANCHOR } from '../settings/WidgetCapsPanel.js'

export type WidgetPausedNoticeProps = {
  readonly widget: WidgetView
}

/** What the page says above everything else when the widget is not serving. */
export function WidgetPausedNotice({ widget }: WidgetPausedNoticeProps) {
  return (
    <Notice role="status">
      <Prose>
        This widget is paused. It is showing its fallback — “{widget.fallback_text}” — and nothing
        new is being recorded. Events already recorded this month still bill.
      </Prose>
    </Notice>
  )
}

export type WidgetCappedNoticeProps = {
  readonly widget: WidgetView
  readonly summary: WidgetSummaryView
}

/**
 * What the page says when the widget has reached its cap.
 *
 * `summary.state` is the field that says whether this widget is serving —
 * `ok | limit_reached | paused` — and it is the premise of §1 of both mockups. A
 * page that read only `widget.status` had two states for three answers, so a widget
 * at its cap said nothing at all while its own site showed the fallback.
 *
 * **Neutral, not ochre.** DESIGN.md P4: ochre is "this needs your decision, or this
 * costs you more", and the cap is the one state where nothing further can be billed
 * (`UsageStateNameSchema`: "past it events are still recorded and never billed").
 * The link is the decision, for whoever wants to make it.
 */
export function WidgetCappedNotice({ widget, summary }: WidgetCappedNoticeProps) {
  const format = useFormat()

  return (
    <Notice role="status">
      <Prose>
        This widget has reached its cap of {format.qty(summary.cap, nounOf(summary))} for{' '}
        {format.monthLong(summary.period)}. It is showing its fallback — “{widget.fallback_text}” —
        and nothing past the cap is recorded against your bill.{' '}
        <Linkish asChild>
          <Link to="/app/settings" hash={WIDGET_CAPS_ANCHOR}>
            Change the cap
          </Link>
        </Linkish>
        .
      </Prose>
    </Notice>
  )
}

export type WidgetActionsProps = {
  readonly widget: WidgetView
}

/**
 * The two buttons under the meter, and the confirmation behind the first of them.
 *
 * They are passed to `WidgetUsagePanel` as its `actions` node, which is where the
 * mockups draw them — under the cap sentence, in the right-hand column.
 */
export function WidgetActions({ widget }: WidgetActionsProps) {
  const [confirming, setConfirming] = useState(false)
  const setPaused = useSetWidgetPaused()
  const principal = usePrincipal()
  const mayManage = abilitiesOf(principal).approvesMoney
  const paused = widget.status === 'paused'

  return (
    <>
      <ButtonRow>
        {!mayManage ? null : paused ? (
          <Button
            size="sm"
            onClick={() => {
              setPaused.mutate({ ref: widget.ref, paused: false })
            }}
            disabled={setPaused.isPending}
          >
            Resume the widget
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setConfirming(true)
            }}
          >
            Pause the widget
          </Button>
        )}

        {/*
          The link stays for everybody: `WidgetCapsPanel` shows the cap read-only to
          a reader who may not set it, so this goes somewhere useful either way. A
          widget not charged per unit has no cap at all (session 46c), so it has no
          link to one.
        */}
        {isMetered(widget.unit_amount.amount) ? (
          <Button size="sm" asChild>
            <Link to="/app/settings" hash={WIDGET_CAPS_ANCHOR}>
              Change the cap
            </Link>
          </Button>
        ) : null}
      </ButtonRow>

      {mayManage ? null : (
        <Small>
          <Muted>
            Pausing a widget stops what it earns, so it is an owner&rsquo;s or an admin&rsquo;s to
            do.
          </Muted>
        </Small>
      )}

      <ConfirmDialog
        open={confirming && mayManage}
        onOpenChange={setConfirming}
        title={`Pause ${widget.name}?`}
        confirmLabel={`Pause ${widget.name}`}
        cancelLabel="Leave it running"
        busy={setPaused.isPending}
        onConfirm={() => {
          setPaused.mutate(
            { ref: widget.ref, paused: true },
            {
              onSuccess: () => {
                setConfirming(false)
              },
            },
          )
        }}
      >
        <Prose>
          {widget.site} will stop serving immediately and show its fallback instead. Events already
          recorded this month still bill, and you can resume it here at any time.
          {isZero(widget.monthly_amount)
            ? null
            : ' While it is paused at the end of a month, that month’s fee is not billed.'}
        </Prose>
      </ConfirmDialog>
    </>
  )
}
